import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { PlayArrowFilled } from '@dsny/dsny-component-library/icons'
import {
  useNoHoverMediaQuery,
  useTabletDownMediaQuery
} from 'utils/hooks/useMediaQueries'
import PlaylistContextMenu from 'components/Dropdowns/PlaylistActionsMenu/PlaylistContextMenu'
import PlaylistMoreActionsDropdown from 'components/Dropdowns/PlaylistActionsMenu/PlaylistMoreActionsDropdown'
import PlaylistCoverMosaic from './PlaylistCoverMosaic'
import {
  Card,
  CardInfo,
  CoverContainer,
  CoverLink,
  CoverShade,
  MoreActions,
  PlayButton,
  SongCount,
  Title,
  TitleLink,
  TitleText,
  TitleRow
} from './PlaylistCard.styles'

const PlaylistTitle = ({ name, playlistId }) => (
  <Title>
    <TitleLink title={name} to={`/playlists/${playlistId}`}>
      <TitleText>{name}</TitleText>
    </TitleLink>
  </Title>
)

PlaylistTitle.propTypes = {
  name: PropTypes.string.isRequired,
  playlistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

const PlaylistCard = ({
  playlist,
  isPlaybackPending = false,
  isRemovalPending = false,
  onPlayClick,
  onRemoveClick
}) => {
  const { t } = useTranslation()
  const isTabletDown = useTabletDownMediaQuery()
  const hasNoHover = useNoHoverMediaQuery()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const songCount = Array.isArray(playlist.songs)
    ? playlist.songs.length
    : Number(playlist.tracks) || 0
  const canPlay = songCount > 0 && !isPlaybackPending

  function handlePlay() {
    if (canPlay) onPlayClick(playlist)
  }

  function handleRemove() {
    onRemoveClick(playlist)
  }

  const removeHandler =
    onRemoveClick && !isRemovalPending ? handleRemove : undefined

  return (
    <PlaylistContextMenu
      onOpenChange={setIsMenuOpen}
      onPlay={canPlay ? handlePlay : undefined}
      onRemove={removeHandler}>
      <Card
        data-actions-open={isMenuOpen}
        data-cy="playlist-card"
        data-playlist-id={playlist.playlistid}>
        <CoverContainer>
          <CoverLink
            aria-hidden="true"
            tabIndex={-1}
            to={`/playlists/${playlist.playlistid}`}>
            <PlaylistCoverMosaic playlist={playlist} />
          </CoverLink>
          <CoverShade />
          <PlayButton
            aria-label={t('general.play')}
            data-cy="playlist-card-play"
            disabled={!canPlay}
            type="button"
            onClick={handlePlay}>
            <PlayArrowFilled aria-hidden="true" height={48} width={48} />
          </PlayButton>
        </CoverContainer>
        <CardInfo>
          <SongCount>{t('playlist.song-count', { count: songCount })}</SongCount>
          <TitleRow>
            <PlaylistTitle name={playlist.name} playlistId={playlist.playlistid} />
            <MoreActions
              $alwaysVisible={isTabletDown || hasNoHover}
              $isOpen={isMenuOpen}>
              <PlaylistMoreActionsDropdown
                onOpenChange={setIsMenuOpen}
                onPlay={canPlay ? handlePlay : undefined}
                onRemove={removeHandler} />
            </MoreActions>
          </TitleRow>
        </CardInfo>
      </Card>
    </PlaylistContextMenu>
  )
}

PlaylistCard.propTypes = {
  playlist: PropTypes.shape({
    playlistid: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    name: PropTypes.string.isRequired,
    songs: PropTypes.arrayOf(PropTypes.object),
    tracks: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  isPlaybackPending: PropTypes.bool,
  isRemovalPending: PropTypes.bool,
  onPlayClick: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func
}

export default PlaylistCard
